import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import Logo, { LOGO_SRC } from "@/components/Logo";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.full_name.split(" ")[0]}`);
      navigate(u.paid || u.is_admin ? "/watch" : "/checkout", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Sign In" subtitle="Access your PPV stream">
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <div>
          <label className="eyebrow block mb-2">Email</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flat"
            data-testid="login-email-input"
            autoComplete="email"
          />
        </div>
        <div>
          <label className="eyebrow block mb-2">Password</label>
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="flat"
            data-testid="login-password-input"
            autoComplete="current-password"
          />
        </div>
        <button
          type="submit"
          disabled={busy}
          className="btn-primary w-full py-4 text-sm flex items-center justify-center gap-2 disabled:opacity-60"
          data-testid="login-submit-btn"
        >
          {busy && <Loader2 className="animate-spin" size={18} />}
          Sign In
        </button>
      </form>
      <p className="mt-6 text-sm text-zinc-400">
        New here?{" "}
        <Link to="/register" className="text-rose-500 underline" data-testid="link-to-register">
          Create an account
        </Link>
      </p>
    </AuthShell>
  );
}

export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#050505] text-white">
      <div className="hidden lg:flex relative overflow-hidden items-center justify-center bg-black">
        <div className="absolute -inset-12 bg-rose-600/15 blur-3xl" />
        <img
          src={LOGO_SRC}
          alt="Corporate Clash in Macarthur"
          className="relative w-[420px] rounded-full ring-1 ring-white/10"
        />
        <div className="absolute bottom-12 left-12 right-12">
          <p className="eyebrow text-rose-500 mb-4">Corporate Clash</p>
          <h2 className="font-display text-5xl leading-none">
            Private.
            <br />
            Paywalled.
            <br />
            Pulse-pounding.
          </h2>
        </div>
      </div>
      <div className="flex items-center justify-center p-8 md:p-16">
        <div className="w-full max-w-md">
          <Link
            to="/"
            className="inline-flex"
            data-testid="back-home-link"
          >
            <Logo size={28} />
          </Link>
          <h1 className="font-display text-6xl mt-10 uppercase">{title}</h1>
          <p className="mt-2 text-zinc-400">{subtitle}</p>
          <div className="mt-10">{children}</div>
        </div>
      </div>
    </div>
  );
}
